<template>
    <v-card>
        <v-alert v-if="currQuery.queries.length == 0" class="mb-0" :color="infoColor" dark icon="mdi-database-search"
            prominent>
            There are currently no database queries configured. In order to run a database query, please add a query or
            queries, or load a saved configuration.
        </v-alert>
        <v-card-text v-else>
            <v-card-subtitle>
                <v-icon :color="infoColor">mdi-database-settings</v-icon> Query Configuration
            </v-card-subtitle>
            <v-card-text>
                <v-form ref="databaseTypeForm" v-model="databaseValid" lazy-validation>
                    <v-select v-model="currQuery.databaseType" dense :items="databaseType" label="Database Type"
                        outlined placeholder="Please select a database type" :rules="databaseTypeRules"
                        @change="setColumns()">
                    </v-select>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn ref="runQueryBtn" :color="successColor" class="white--text" :disabled="!valid || !databaseValid"
                    @click="runQuery">
                    <v-icon>mdi-database-search</v-icon> Run Query
                </v-btn>
            </v-card-actions>
        </v-card-text>
        <v-divider v-if="currQuery.id" class="mx-5"></v-divider>
        <v-card-text v-if="currQuery.id">
            <v-card-subtitle class="pt-0">
                <v-icon :color="infoColor">mdi-information</v-icon> Saved Query Information
            </v-card-subtitle>
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="2" class="font-weight-bold py-1">
                        Created By
                    </v-col>
                    <v-col cols="12" md="10" class="py-1">
                        {{ currQuery.createdByUsername || 'N/A'}}
                    </v-col>
                    <v-col cols="12" md="2" class="font-weight-bold py-1">
                        Date Created
                    </v-col>
                    <v-col cols="12" md="10" class="py-1">
                        {{ currQuery.createdTimestamp ? new Date(currQuery.createdTimestamp * 1000).toLocaleString() : 'N/A'}}
                    </v-col>
                    <v-col cols="12" md="2" class="font-weight-bold py-1">
                        Name
                    </v-col>
                    <v-col cols="12" md="10" class="py-1">
                        {{ currQuery.name || 'N/A'}}
                    </v-col>
                    <v-col cols="12" md="2" class="font-weight-bold py-1">
                        Description
                    </v-col>
                    <v-col cols="12" md="10" class="py-1">
                        {{ currQuery.description || 'N/A'}}
                    </v-col>
                    <v-col cols="12" md="2" class="font-weight-bold py-1">
                        Database Type
                    </v-col>
                    <v-col cols="12" md="10" class="py-1">
                        {{ currQuery.databaseType || 'N/A'}}
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card-text>
        <v-card-text v-for="(query, i) in currQuery.queries" :key="i" class="pa-4">
            <v-card elevation="0" outlined>
                <v-toolbar flat>
                    Query {{ i + 1 }}
                    <v-spacer></v-spacer>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" class="mr-2 white--text" :color="primaryColor" small
                                @click="addColumn(query)">
                                <v-icon>mdi-table-column-plus-before</v-icon>
                            </v-btn>
                        </template>
                        <span>Add Column</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" class="white--text" :color="dangerColor" small
                                @click="openRemoveDialog(i)">
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </template>
                        <span>Remove Query</span>
                    </v-tooltip>
                </v-toolbar>
                <v-card-text class=pt-0>
                    <v-row>
                        <v-col v-for="(column, j) in query.columns" :key="j" cols="12" md="3" lg="2"
                            class="d-flex flex-column">
                            <div>
                                <v-card-subtitle class="font-weight-bold pb-1 pt-0 px-0">Column {{ j + 1 }} <v-btn
                                        :color="dangerColor" icon @click="removeColumn(query, j)">
                                        <v-icon>mdi-delete</v-icon>
                                    </v-btn>
                                </v-card-subtitle>
                                <v-select v-model="column.field" dense hide-details :items="columns" label="Field"
                                    outlined :rules="fieldRules" @change="setColumn(column, columns)"></v-select>
                                <v-select v-if="column.options" v-model="column.subfield" dense hide-details
                                    :items="column.options" label="Subfield" outlined :rules="subfieldRules"
                                    @change="setTerm(column)"></v-select>
                                <v-select v-if="column.type == 'date'" v-model="column.compare" dense hide-details
                                    :items="compareDate" label="Compare" outlined :rules="compareRules"></v-select>
                                <v-select v-else-if="column.type == 'number'" v-model="column.compare" dense
                                    hide-details :items="compareNumber" label="Compare" outlined :rules="compareRules">
                                </v-select>
                                <v-select v-else v-model="column.compare" dense hide-details :items="compareText"
                                    label="Compare" outlined :rules="compareRules"></v-select>
                                <v-menu v-if="column.type == 'date'" :ref="getDateRef(j)"
                                    :close-on-content-click="false" :return-value.sync="column.term"
                                    transition="scale-transition" offset-y max-width="290px" min-width="auto">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="column.term" dense hide-details label="Term" outlined
                                            readonly v-bind="attrs" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="column.term" no-title scrollable>
                                        <v-spacer></v-spacer>
                                        <v-btn text color="primary" @click="setDateRef(j, column.term)">
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-menu>
                                <v-text-field v-else-if="column.type == 'number'" v-model="column.term" dense
                                    hide-details label="Term" outlined type="number">
                                </v-text-field>
                                <v-text-field v-else v-model="column.term" dense hide-details label="Term" outlined>
                                </v-text-field>
                            </div>
                            <div class="mt-auto">
                                <v-checkbox v-model="column.show" dense false-value="0" hide-details label="Show"
                                    outlined true-value="1">
                                </v-checkbox>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-card-text>
        <v-dialog v-model="removeDialog" width="500">
            <v-card>
                <v-card-title>Remove Query {{ removeIndex + 1 }}</v-card-title>
                <v-card-text>
                    Are you sure you would like remove this database query? This action cannot be undone! Please click
                    confirm to proceed.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn ref="removeBtn" class="white--text" :color="dangerColor" @click="removeQuery()">
                        Confirm
                    </v-btn>
                    <v-btn :color="warningColor" @click="closeRemoveDialog()">
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
export default {
    name: 'reports-query',
    props: {
        currQuery: {
            required: true
        },
        data: {
            required: true
        },
        legacyColumns: {
            required: true,
            type: Array
        },
        modernColumns: {
            required: true,
            type: Array
        },
        valid: {
            required: true,
            type: Boolean
        }
    },
    data() {
        return {
            columns: [],
            compareDate: [{
                    text: 'Any',
                    value: 'any'
                },
                {
                    text: 'Before',
                    value: 'Before'
                },
                {
                    text: 'On',
                    value: 'On'
                },
                {
                    text: 'After',
                    value: 'After'
                }
            ],
            compareNumber: [{
                    text: 'Any',
                    value: 'Any'
                },
                {
                    text: 'Greater than',
                    value: 'Greater than'
                },
                {
                    text: 'Less than',
                    value: 'Less than'
                },
                {
                    text: 'Equal to',
                    value: 'Equal to'
                },
                {
                    text: 'Not equal to',
                    value: 'Not equal to'
                }
            ],
            compareText: [{
                    text: 'Any',
                    value: 'Any'
                },
                {
                    text: 'Contains',
                    value: 'Contains'
                },
                {
                    text: 'Does not contain',
                    value: 'Does not contain'
                },
                {
                    text: 'Starts with',
                    value: 'Starts with'
                },
                {
                    text: 'Ends with',
                    value: 'Ends with'
                }
            ],
            compareRules: [
                v => !!v || 'Compare is required'
            ],
            databaseType: [{
                    text: 'Modern',
                    value: 'modern'
                },
                {
                    text: 'Legacy',
                    value: 'legacy'
                }
            ],
            databaseTypeRules: [
                v => !!v || 'Database type is required'
            ],
            databaseValid: true,
            fieldRules: [
                v => !!v || 'Field is required'
            ],
            menu: false,
            removeDialog: false,
            removeIndex: '',
            subfieldRules: [
                v => !!v || 'Subfield is required'
            ],
            termRules: [
                v => !!v || 'Term is required'
            ],
        }
    },
    methods: {
        addColumn(query) {
            this.$emit('addColumn', query);
        },
        closeRemoveDialog() {
            this.removeDialog = false;
        },
        getDateRef(index) {
            return `dateMenu${index}`;
        },
        openRemoveDialog(index) {
            this.removeDialog = true;
            this.removeIndex = index;
        },
        removeColumn(query, index) {
            this.$emit('removeColumn', query, index);
        },
        removeQuery() {
            this.$emit('removeQuery', this.removeIndex);
            this.closeRemoveDialog();
        },
        runQuery() {
            if (this.$refs.databaseTypeForm.validate()) {
                this.$emit('runQuery');
            }
        },
        setBtnLoader(bool) {
            this.$refs.runQueryBtn.loading = bool;
        },
        setColumn(column, columns) {
            // Iterate through the data and retrieve 
            let field = columns.find(item => item.value == column.field);

            // Set column options and type
            if (field) {
                column.options = field.options ? field.options : '';

                if (!column.options) {
                    column.subfield = '';
                }
                column.label = field.text;
                column.type = field.type;
            }

            // Reset default
            column.compare = '';
            column.term = '';
        },
        setColumns() {
            if (this.currQuery.databaseType == 'legacy') {
                this.columns = this.legacyColumns;
            } else {
                this.columns = this.modernColumns;
            }
        },
        setDateRef(index, value) {
            let dateMenu = this.$refs['dateMenu' + index][0];

            // Set date field value
            if (dateMenu) {
                dateMenu.save(value);
            }
        },
        setTerm(column) {
            if (column.subfield) {
                column.term = column.subfield;
            }
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        infoColor() {
            return '#5578eb';
        },
        primaryColor() {
            return '#5867dd';
        },
        successColor() {
            return '#1dc9b7';
        },
        warningColor() {
            return '#ffb822'
        }
    }
}
</script>

<style>
.v-input--checkbox {
    margin-top: 0 !important;
}

.v-text-field,
.v-select {
    margin-bottom: 0.5rem !important;
}
</style>